import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';

/**
 * Interfaz para puntos genéricos en el mapa
 */
export interface IPuntoGenerico {
  _id?: string;
  coordenadas: [number, number];
  text?: string;
  [key: string]: any;
}

/**
 * PuntosLayerManager
 *
 * Manager para renderizar puntos genéricos en el mapa (puntoSimple).
 * Se usa como fallback cuando no hay un tipo específico de entidad.
 *
 * Características:
 * - Icono simple de pin genérico
 * - NO soporta clustering (son puntos únicos)
 * - Icono: pin genérico rojo
 */
@Injectable({
  providedIn: 'root',
})
export class PuntosLayerManager extends BaseEntityLayerManager<IPuntoGenerico> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'puntos';
  readonly supportsClustering = false;
  readonly clusterColor = '#808080'; // Gris (no se usa pero es requerido)

  // IDs de layers
  private mainLayerId = 'puntos-layer';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un punto genérico a un Feature de GeoJSON
   */
  override entityToFeature(
    punto: IPuntoGenerico,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(punto);

    const coordinates = punto.coordenadas || [-58.443832, -34.587386];

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: punto._id,
        text: punto.text,
        iconoId: punto['iconoId'] ?? null,
        iconSize: punto['iconSize'] ?? null,
      },
    };
  }

  /**
   * Icono dinámico: usa iconoId de la feature si está presente, sino pin genérico
   */
  override getIconExpression(): mapboxgl.Expression | string {
    return [
      'coalesce',
      ['get', 'iconoId'],
      'pin-generico-icon',
    ] as mapboxgl.Expression;
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea el layer para puntos genéricos
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // Main layer (sin clustering)
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': [
          'coalesce',
          ['get', 'iconSize'],
          0.068,
        ] as mapboxgl.Expression,
        'icon-allow-overlap': true,
        'icon-padding': 0,
      },
    });
  }

  // ========== VISIBILIDAD ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.map.getLayer(this.mainLayerId)) {
      this.map.setLayoutProperty(
        this.mainLayerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }
}
